import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { registerServiceWorker } from './utils/offline';
import { useWebSocket } from './utils/useWebSocket';
import api from './utils/api';
import './App.css';
import { BrandingProvider } from './contexts/BrandingContext';

// Auth Components — kept eager: the auth flow must be instant, and AuthCallback
// is rendered outside <Routes> (so it can't live inside the Suspense boundary).
import Login from './pages/Login';
import TwoFactor from './pages/TwoFactor';
import Signup from './pages/Signup';
import AuthCallback from './pages/AuthCallback';
import Layout from './components/Layout';

// Every other page is route-level code-split via React.lazy, so each loads as
// its own JS chunk on demand — shrinking the initial bundle and speeding first
// paint. They all render inside the <Suspense> boundary around <Routes>.
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const AcceptInvite = React.lazy(() => import('./pages/AcceptInvite'));
const SubscriptionExpired = React.lazy(() => import('./pages/SubscriptionExpired'));

// Legal / Public Pages
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const LicenseAgreement = React.lazy(() => import('./pages/LicenseAgreement'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));

// Protected Components
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Projects = React.lazy(() => import('./pages/Projects'));
const Sites = React.lazy(() => import('./pages/Sites'));
const Forms = React.lazy(() => import('./pages/Forms'));
const FormBuilder = React.lazy(() => import('./pages/FormBuilder'));
const FormSubmission = React.lazy(() => import('./pages/FormSubmission'));
const Tasks = React.lazy(() => import('./pages/Tasks'));
const ODSI = React.lazy(() => import('./pages/ODSI'));
const Users = React.lazy(() => import('./pages/Users'));
const Profile = React.lazy(() => import('./pages/Profile'));
const ISO45001 = React.lazy(() => import('./pages/ISO45001'));
const BehavioralIntelligence = React.lazy(() => import('./pages/BehavioralIntelligence'));
const Reports = React.lazy(() => import('./pages/Reports'));
const DemeritReport = React.lazy(() => import('./pages/DemeritReport'));
const Billing = React.lazy(() => import('./pages/Billing'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const Referrals = React.lazy(() => import('./pages/Referrals'));

// Workers Voice Components
const WorkersVoice = React.lazy(() => import('./pages/WorkersVoice'));
const SurveyBuilder = React.lazy(() => import('./pages/SurveyBuilder'));
const InstructionsManagement = React.lazy(() => import('./pages/InstructionsManagement'));
const LeadershipAnalytics = React.lazy(() => import('./pages/LeadershipAnalytics'));

// Onboarding Wizard
const OnboardingWizard = React.lazy(() => import('./pages/OnboardingWizard'));

// New Feature Pages
const SignatureDashboard = React.lazy(() => import('./pages/SignatureDashboard'));
const TeamManagement = React.lazy(() => import('./pages/TeamManagement'));
const UserGuide = React.lazy(() => import('./pages/UserGuide'));
const MeritDemerit = React.lazy(() => import('./pages/MeritDemerit'));
const DocumentCompliance = React.lazy(() => import('./pages/DocumentCompliance'));
const ExecutiveReports = React.lazy(() => import('./pages/ExecutiveReports'));
const ComplianceDashboard = React.lazy(() => import('./pages/ComplianceDashboard'));
const ComplianceLibrary = React.lazy(() => import('./pages/ComplianceLibrary'));
const ComplianceCalendar = React.lazy(() => import('./pages/ComplianceCalendar'));
const SignalCheck = React.lazy(() => import('./pages/SignalCheck'));
const SignalDashboard = React.lazy(() => import('./pages/SignalDashboard'));
const Methodology = React.lazy(() => import('./pages/Methodology'));
const ITOTConvergence = React.lazy(() => import('./pages/ITOTConvergence'));
const OrgStructure = React.lazy(() => import('./pages/OrgStructure'));
const Governance = React.lazy(() => import('./pages/Governance'));
const ISOAuditChecklist = React.lazy(() => import('./pages/ISOAuditChecklist'));
const Settings = React.lazy(() => import('./pages/Settings'));
const SecuritySettings = React.lazy(() => import('./pages/SecuritySettings'));
const ThisWasntMe = React.lazy(() => import('./pages/ThisWasntMe'));
const ApproveLogin = React.lazy(() => import('./pages/ApproveLogin'));
const MetricsHealth = React.lazy(() => import('./pages/MetricsHealth'));
const Marketplace = React.lazy(() => import('./pages/Marketplace'));
const AdminFreelancers = React.lazy(() => import('./pages/AdminFreelancers'));

// PDPA Compliance
import PDPAConsentModal from './components/PDPAConsentModal';

// What's New splash (shown once per day after login)
import WhatsNewSplash from './components/WhatsNewSplash';
import { useSuppressModalsOnRoutes } from './hooks/useSuppressModalsOnRoutes';

// Security: idle-logout + sliding token refresh + screen-lock email alert.
import IdleLogoutManager from './components/IdleLogoutManager';

// Catches failed lazy-chunk loads (e.g. after a redeploy) → friendly refresh prompt.
import RouteErrorBoundary from './components/RouteErrorBoundary';

function ProtectedRoute({ children }) {
  const { isAuthenticated, getToken } = require('@/utils/auth');
  const location = useLocation();
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [, setPdpaChecked] = useState(false);
  const [showPdpaModal, setShowPdpaModal] = useState(false);
  const suppressModals = useSuppressModalsOnRoutes();
  const token = getToken();
  const authenticated = isAuthenticated();
  
  useEffect(() => {
    const checkSubscription = async () => {
      if (token) {
        try {
          const response = await api.get('/subscription/status');
          setSubscriptionActive(response.data.active);
          setSubscriptionStatus(response.data.subscription_status);
        } catch (error) {
          // If 402 error, subscription expired
          if (error.response?.status === 402) {
            setSubscriptionActive(false);
          }
        }
      }
      setSubscriptionChecked(true);
    };
    
    checkSubscription();
  }, [token]);
  
  // Check PDPA consent.
  // The backend returns `defer=true` while the org is still in the signup → payment →
  // onboarding flow so the modal never interrupts first-run setup. It will naturally
  // appear on the next route change (e.g., /dashboard) once onboarding completes and
  // any pending payment clears.
  useEffect(() => {
    const checkPdpaConsent = async () => {
      if (token && subscriptionChecked && subscriptionActive) {
        try {
          const response = await api.get('/pdpa/my-consent');
          const { consent_given, needs_reconsent, defer } = response.data || {};
          if (!defer && (!consent_given || needs_reconsent)) {
            setShowPdpaModal(true);
          } else {
            setShowPdpaModal(false);
          }
        } catch (error) {
          console.error('Failed to check PDPA consent:', error);
        }
      }
      setPdpaChecked(true);
    };

    if (subscriptionChecked) {
      checkPdpaConsent();
    }
  }, [token, subscriptionChecked, subscriptionActive, location.pathname]);
  
  // Check both token and full authentication
  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Wait for subscription check
  if (!subscriptionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Route pending_payment users (pay_now signups that haven't completed Stripe
  // checkout) AND grace_period users (trial ended, 14-day pay-or-be-deleted
  // window) to /billing so they can pay — they are otherwise locked out.
  if (
    (subscriptionStatus === 'pending_payment' || subscriptionStatus === 'grace_period') &&
    location.pathname !== '/billing'
  ) {
    return <Navigate to={`/billing?status=${subscriptionStatus}`} replace />;
  }

  // Redirect to subscription page if expired (real expiry — trial/subscription lapsed)
  if (
    !subscriptionActive &&
    subscriptionStatus !== 'pending_payment' &&
    subscriptionStatus !== 'grace_period'
  ) {
    return <Navigate to="/subscription-expired" replace />;
  }
  
  return (
    <>
      {/* PDPA + "What's New" splash are suppressed on transactional / focused
          routes (billing, checkout, onboarding, guide, signal-check, …) via
          useSuppressModalsOnRoutes — so they never interrupt a delicate flow
          or overlap the page the user is working on. */}
      <PDPAConsentModal 
        open={showPdpaModal && !suppressModals} 
        onComplete={() => setShowPdpaModal(false)} 
      />
      {/* Daily "What's New" splash — self-gates via localStorage, so it renders
          at most once per calendar day per device. */}
      {!showPdpaModal && !suppressModals && <WhatsNewSplash />}
      <Layout>{children}</Layout>
    </>
  );
}

function PublicRoute({ children }) {
  const { getToken } = require('@/utils/auth');
  const token = getToken();
  if (token) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function App() {
  // Register service worker for PWA
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <BrandingProvider>
          <AppContent />
        </BrandingProvider>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

// Separate component inside BrowserRouter to use hooks and WebSocket
function AppContent() {
  const location = useLocation();
  
  // Initialize WebSocket connection (only when logged in)
  useWebSocket();
  
  // Handle OAuth callback - check for session_id in hash OR /auth path
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=') || location.pathname === '/auth') {
    return <AuthCallback />;
  }
  
  return (
    <>
      <IdleLogoutManager />
      <RouteErrorBoundary>
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center" data-testid="route-loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      }>
      <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/auth" element={<AuthCallback />} />
      <Route path="/two-factor" element={<TwoFactor />} />
          
          {/* Public Routes */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
          <Route path="/accept-invite" element={<PublicRoute><AcceptInvite /></PublicRoute>} />
          <Route path="/subscription-expired" element={<SubscriptionExpired />} />
          
          {/* Legal Pages (Public) */}
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/license-agreement" element={<LicenseAgreement />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
          <Route path="/sites" element={<ProtectedRoute><Sites /></ProtectedRoute>} />
          <Route path="/forms" element={<ProtectedRoute><Forms /></ProtectedRoute>} />
          <Route path="/forms/builder" element={<ProtectedRoute><FormBuilder /></ProtectedRoute>} />
          <Route path="/forms/submit/:formId" element={<ProtectedRoute><FormSubmission /></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
          <Route path="/odsi" element={<ProtectedRoute><ODSI /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/iso45001" element={<ProtectedRoute><ISO45001 /></ProtectedRoute>} />
          <Route path="/iso45001/audit" element={<ProtectedRoute><ISOAuditChecklist /></ProtectedRoute>} />
          <Route path="/intelligence" element={<ProtectedRoute><BehavioralIntelligence /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          <Route path="/reports/demerits" element={<ProtectedRoute><DemeritReport /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><FAQ /></ProtectedRoute>} />
          <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
          
          {/* Workers Voice Routes */}
          <Route path="/workers-voice" element={<ProtectedRoute><WorkersVoice /></ProtectedRoute>} />
          <Route path="/surveys" element={<ProtectedRoute><SurveyBuilder /></ProtectedRoute>} />
          <Route path="/instructions" element={<ProtectedRoute><InstructionsManagement /></ProtectedRoute>} />
          <Route path="/leadership" element={<ProtectedRoute><LeadershipAnalytics /></ProtectedRoute>} />
          
          {/* Onboarding Wizard */}
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
          
          {/* New Feature Routes */}
          <Route path="/signatures" element={<ProtectedRoute><SignatureDashboard /></ProtectedRoute>} />
          <Route path="/teams" element={<ProtectedRoute><TeamManagement /></ProtectedRoute>} />
          <Route path="/guide" element={<ProtectedRoute><UserGuide /></ProtectedRoute>} />
          <Route path="/merit-demerit" element={<ProtectedRoute><MeritDemerit /></ProtectedRoute>} />
          <Route path="/document-compliance" element={<ProtectedRoute><DocumentCompliance /></ProtectedRoute>} />
          <Route path="/executive-reports" element={<ProtectedRoute><ExecutiveReports /></ProtectedRoute>} />
          <Route path="/compliance-dashboard" element={<ProtectedRoute><ComplianceDashboard /></ProtectedRoute>} />
          <Route path="/itot-convergence" element={<ProtectedRoute><ITOTConvergence /></ProtectedRoute>} />          <Route path="/compliance-library" element={<ProtectedRoute><ComplianceLibrary /></ProtectedRoute>} />
          <Route path="/compliance-calendar" element={<ProtectedRoute><ComplianceCalendar /></ProtectedRoute>} />
          
          {/* Signal Architecture Routes */}
          <Route path="/signal-check" element={<ProtectedRoute><SignalCheck /></ProtectedRoute>} />
          <Route path="/signal-dashboard" element={<ProtectedRoute><SignalDashboard /></ProtectedRoute>} />
          <Route path="/methodology" element={<ProtectedRoute><Methodology /></ProtectedRoute>} />
          <Route path="/governance" element={<ProtectedRoute><Governance /></ProtectedRoute>} />
          
          {/* Settings */}
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/org-structure" element={<ProtectedRoute><OrgStructure /></ProtectedRoute>} />
          <Route path="/settings/security" element={<ProtectedRoute><SecuritySettings /></ProtectedRoute>} />
          <Route path="/security/this-wasnt-me" element={<ThisWasntMe />} />
          <Route path="/auth/approve-login" element={<ApproveLogin />} />
          <Route path="/admin/metrics-health" element={<ProtectedRoute><MetricsHealth /></ProtectedRoute>} />
          <Route path="/admin/freelancers" element={<ProtectedRoute><AdminFreelancers /></ProtectedRoute>} />
          <Route path="/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
        </Routes>
      </Suspense>
      </RouteErrorBoundary>
      </>
      );
    }

export default App;